import { useEffect, useState } from "react";

export type AppRouteName =
  | "landing"
  | "login"
  | "dashboard"
  | "profile"
  | "company"
  | "companyMembers"
  | "companyFiles"
  | "companyModeration"
  | "specialists"
  | "specialistProfile"
  | "specialistSkills"
  | "specialistExperience"
  | "specialistPortfolio"
  | "specialistEducation"
  | "specialistRates"
  | "specialistAvailability"
  | "resume"
  | "jobs"
  | "matching"
  | "assignments"
  | "dictionaries"
  | "not-found";

export type WorkspaceRouteName = Exclude<AppRouteName, "landing" | "login" | "not-found">;

export interface AppRoute {
  name: AppRouteName;
  pathname: string;
  protected: boolean;
}

export const workspaceRoutePaths: Record<WorkspaceRouteName, string> = {
  dashboard: "/app",
  profile: "/app/profile",
  company: "/app/company",
  companyMembers: "/app/company/members",
  companyFiles: "/app/company/files",
  companyModeration: "/app/admin/company-moderation",
  specialists: "/app/specialists",
  specialistProfile: "/app/specialist/profile",
  specialistSkills: "/app/specialist/skills",
  specialistExperience: "/app/specialist/experience",
  specialistPortfolio: "/app/specialist/portfolio",
  specialistEducation: "/app/specialist/education",
  specialistRates: "/app/specialist/rates",
  specialistAvailability: "/app/specialist/availability",
  resume: "/app/specialist/resume",
  jobs: "/app/jobs",
  matching: "/app/matching",
  assignments: "/app/assignments",
  dictionaries: "/app/admin/dictionaries",
};

const protectedRoutes = Object.fromEntries(
  Object.entries(workspaceRoutePaths).map(([name, pathname]) => [pathname, name]),
) as Record<string, WorkspaceRouteName>;

function normalizePathname(pathname: string): string {
  if (!pathname.startsWith("/")) return `/${pathname}`;
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
}

export function resolveRoute(pathname: string): AppRoute {
  const normalized = normalizePathname(pathname);

  if (normalized === "/") return { name: "landing", pathname: normalized, protected: false };
  if (normalized === "/login") return { name: "login", pathname: normalized, protected: false };

  const protectedRoute = protectedRoutes[normalized];
  if (protectedRoute) return { name: protectedRoute, pathname: normalized, protected: true };

  return { name: "not-found", pathname: normalized, protected: false };
}

export function isWorkspaceRouteName(name: AppRouteName): name is WorkspaceRouteName {
  return name !== "landing" && name !== "login" && name !== "not-found";
}

export function navigate(pathname: string, options: { replace?: boolean } = {}): void {
  const normalized = normalizePathname(pathname);

  if (options.replace) {
    window.history.replaceState(null, "", normalized);
  } else {
    window.history.pushState(null, "", normalized);
  }

  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function usePathname(): string {
  const [pathname, setPathname] = useState(() => window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return pathname;
}
